import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  User, 
  Calendar, 
  CheckCircle, 
  X, 
  Eye,
  Save,
  Search,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react';

const FileUploads = ({ onBack }) => {
  const [patients, setPatients] = useState([
    { 
      id: 1, 
      patientCode: 'PT001', 
      firstName: 'Rajesh', 
      lastName: 'Kumar', 
      age: 35, 
      gender: 'Male',
      tests: [
        { id: 1, name: 'Complete Blood Count (CBC)', status: 'completed' },
        { id: 2, name: 'Blood Glucose Fasting', status: 'completed' }
      ]
    },
    { 
      id: 2, 
      patientCode: 'PT002', 
      firstName: 'Priya', 
      lastName: 'Sharma', 
      age: 28, 
      gender: 'Female',
      tests: [
        { id: 3, name: 'Thyroid Profile', status: 'completed' }
      ]
    }
  ]);

  const [formData, setFormData] = useState({
    recordDate: new Date().toISOString().split('T')[0],
    selectedPatient: null,
    selectedTests: [],
    uploadedFiles: {},
    doctorName: 'Dr. John Smith'
  });

  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [filteredPatients, setFilteredPatients] = useState(patients);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const dropdownRef = useRef(null);
  const alertTimeoutRef = useRef(null);

  // Define color schemes for different test sections
  const testColorSchemes = [
    { 
      bg: 'bg-blue-50', 
      border: 'border-blue-200', 
      header: 'text-blue-800', 
      icon: 'text-blue-600',
      gradient: 'from-blue-500 to-blue-600'
    },
    { 
      bg: 'bg-green-50', 
      border: 'border-green-200', 
      header: 'text-green-800', 
      icon: 'text-green-600',
      gradient: 'from-green-500 to-green-600'
    },
    { 
      bg: 'bg-purple-50', 
      border: 'border-purple-200', 
      header: 'text-purple-800', 
      icon: 'text-purple-600',
      gradient: 'from-purple-500 to-purple-600'
    },
    { 
      bg: 'bg-amber-50', 
      border: 'border-amber-200', 
      header: 'text-amber-800', 
      icon: 'text-amber-600',
      gradient: 'from-amber-500 to-amber-600'
    },
    { 
      bg: 'bg-rose-50', 
      border: 'border-rose-200', 
      header: 'text-rose-800', 
      icon: 'text-rose-600',
      gradient: 'from-rose-500 to-rose-600'
    }
  ];

  // Function to get color scheme based on test index
  const getTestColorScheme = (index) => {
    return testColorSchemes[index % testColorSchemes.length];
  };

  useEffect(() => {
    if (patientSearch) {
      const filtered = patients.filter(patient => 
        patient.firstName.toLowerCase().includes(patientSearch.toLowerCase()) ||
        patient.lastName.toLowerCase().includes(patientSearch.toLowerCase()) ||
        patient.patientCode.toLowerCase().includes(patientSearch.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [patientSearch, patients]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, []);

  const showAlert = (type, message, duration = 5000) => {
    // Clear any existing timeout
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
    
    setAlert({ show: true, type, message });
    
    // Auto-hide after duration
    alertTimeoutRef.current = setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, duration);
  };

  const handleSelectPatient = (patient) => {
    setFormData({
      ...formData,
      selectedPatient: patient,
      selectedTests: [],
      uploadedFiles: {}
    });
    setShowPatientDropdown(false);
    setPatientSearch(`${patient.patientCode} - ${patient.firstName} ${patient.lastName}`);
  };

  const clearSelectedPatient = () => {
    setFormData({
      ...formData,
      selectedPatient: null,
      selectedTests: [],
      uploadedFiles: {}
    });
    setPatientSearch('');
  };

  const handleTestSelection = (test) => {
    setFormData(prev => {
      const isSelected = prev.selectedTests.some(t => t.id === test.id);
      
      if (isSelected) {
        // Remove test and its files
        const newUploadedFiles = {...prev.uploadedFiles};
        delete newUploadedFiles[test.id];
        
        return {
          ...prev,
          selectedTests: prev.selectedTests.filter(t => t.id !== test.id),
          uploadedFiles: newUploadedFiles
        };
      } else {
        // Add test with empty file structure
        return {
          ...prev,
          selectedTests: [...prev.selectedTests, test],
          uploadedFiles: {
            ...prev.uploadedFiles,
            [test.id]: {
              dicom: null,
              jpeg: null,
              meta: null
            }
          }
        };
      }
    });
  };

  const handleFileUpload = (testId, fileType, file) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: {
        ...prev.uploadedFiles,
        [testId]: {
          ...prev.uploadedFiles[testId],
          [fileType]: file
        }
      }
    }));
  };

  const removeFile = (testId, fileType) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: {
        ...prev.uploadedFiles,
        [testId]: {
          ...prev.uploadedFiles[testId],
          [fileType]: null
        }
      }
    }));
  };

  const viewFile = (file) => {
    // In a real application, this would open the file in a viewer
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
  };

  const handleSave = () => {
    if (!formData.selectedPatient) {
      showAlert('error', 'Please select a patient');
      return;
    }
    
    if (formData.selectedTests.length === 0) {
      showAlert('error', 'Please select at least one test');
      return;
    }
    
    // Validate that all selected tests have all required files
    const incompleteTests = formData.selectedTests.filter(test => {
      const testFiles = formData.uploadedFiles[test.id];
      return !testFiles.dicom || !testFiles.jpeg || !testFiles.meta;
    });
    
    if (incompleteTests.length > 0) {
      showAlert('error', 'Please upload all required files for all selected tests');
      return;
    }
    
    console.log('Saving file uploads:', formData);
    showAlert('success', 'Files uploaded successfully!');
    
    // Reset form after successful upload
    setTimeout(() => {
      setFormData({
        ...formData,
        selectedPatient: null,
        selectedTests: [],
        uploadedFiles: {}
      });
      setPatientSearch('');
    }, 2000);
  };

  const FileUploadField = ({ testId, fileType, label, accept, colorScheme }) => {
    const file = formData.uploadedFiles[testId]?.[fileType];
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
        {!file ? (
          <div className="flex items-center justify-center w-full">
            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed ${colorScheme.border} rounded-lg cursor-pointer hover:${colorScheme.bg}`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className={`w-8 h-8 mb-3 ${colorScheme.icon}`} />
                <p className="mb-2 text-sm text-gray-500">Click to upload</p>
              </div>
              <input
                type="file"
                className="hidden"
                accept={accept}
                onChange={(e) => handleFileUpload(testId, fileType, e.target.files[0])}
              />
            </label>
          </div>
        ) : (
          <div className={`flex items-center justify-between p-3 border ${colorScheme.border} rounded-lg ${colorScheme.bg}`}>
            <div className="flex items-center">
              <FileText className={`w-5 h-5 ${colorScheme.icon} mr-2`} />
              <span className="text-sm font-medium">{file.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => viewFile(file)}
                className="p-1 text-blue-600 hover:text-blue-800"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => removeFile(testId, fileType)}
                className="p-1 text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Alert component
  const Alert = () => {
    if (!alert.show) return null;
    
    const alertConfig = {
      success: {
        bg: 'bg-green-50 border-green-200',
        text: 'text-green-800',
        icon: <CheckCircle2 className="w-5 h-5 text-green-600" />
      },
      error: {
        bg: 'bg-red-50 border-red-200',
        text: 'text-red-800',
        icon: <AlertCircle className="w-5 h-5 text-red-600" />
      },
      info: {
        bg: 'bg-blue-50 border-blue-200',
        text: 'text-blue-800',
        icon: <Info className="w-5 h-5 text-blue-600" />
      }
    };
    
    const config = alertConfig[alert.type] || alertConfig.info;
    
    return (
      <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
        <div className={`flex items-start p-4 rounded-lg border ${config.bg} shadow-lg transform transition-all duration-300 ease-in-out`}>
          <div className="flex-shrink-0 mr-3">
            {config.icon}
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium ${config.text}`}>
              {alert.message}
            </p>
          </div>
          <button
            onClick={() => setAlert({ show: false, type: '', message: '' })}
            className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Alert />
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold">File Uploads</h1>
                <p className="text-blue-100">Upload diagnostic files for completed tests</p>
              </div>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Upload className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Doctor Name (Auto-filled) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Doctor Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={formData.doctorName}
                readOnly
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Automatically filled with logged-in user</p>
          </div>

          {/* Record Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Record Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={formData.recordDate}
                onChange={(e) => setFormData({...formData, recordDate: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Patient Selection */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Patient <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value);
                  setShowPatientDropdown(true);
                }}
                onFocus={() => setShowPatientDropdown(true)}
                placeholder="Search patient by name or code..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {formData.selectedPatient ? (
                <X 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 cursor-pointer hover:text-gray-600"
                  onClick={clearSelectedPatient}
                />
              ) : (
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              )}
            </div>
            {showPatientDropdown && filteredPatients.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredPatients.map(patient => (
                  <div
                    key={patient.id}
                    onClick={() => handleSelectPatient(patient)}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium">{patient.firstName} {patient.lastName}</div>
                    <div className="text-sm text-gray-600">
                      {patient.patientCode} • {patient.age}y • {patient.gender}
                    </div>
                    <div className="text-xs text-gray-500">
                      Completed Tests: {patient.tests.filter(t => t.status === 'completed').map(t => t.name).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Patient Details */}
          {formData.selectedPatient && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center justify-between">
                <div className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Selected Patient
                </div>
                <button
                  onClick={clearSelectedPatient}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Change Patient
                </button>
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Name</div>
                  <div className="font-medium">
                    {formData.selectedPatient.firstName} {formData.selectedPatient.lastName}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Patient Code</div>
                  <div className="font-medium">{formData.selectedPatient.patientCode}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Age & Gender</div>
                  <div className="font-medium">
                    {formData.selectedPatient.age}y, {formData.selectedPatient.gender}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Test Selection */}
          {formData.selectedPatient && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">
                Select Completed Tests for File Upload
              </h3>
              <div className="space-y-2">
                {formData.selectedPatient.tests
                  .filter(test => test.status === 'completed')
                  .map(test => (
                    <div key={test.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={formData.selectedTests.some(t => t.id === test.id)}
                        onChange={() => handleTestSelection(test)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 flex-1">
                        {test.name}
                      </span>
                      {formData.selectedTests.some(t => t.id === test.id) && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* File Uploads for Selected Tests - UPDATED LAYOUT */}
          {formData.selectedTests.map((test, index) => {
            const colorScheme = getTestColorScheme(index);
            return (
              <div key={test.id} className={`border ${colorScheme.border} rounded-lg p-4 ${colorScheme.bg}`}>
                <h3 className={`font-semibold ${colorScheme.header} mb-4 flex items-center`}>
                  <FileText className={`w-5 h-5 mr-2 ${colorScheme.icon}`} />
                  Upload Files for: {test.name}
                </h3>
                
                {/* Updated: Upload fields in a grid row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FileUploadField 
                    testId={test.id}
                    fileType="dicom"
                    label="DICOM File Upload"
                    accept=".jpg,.jpeg,.png"
                    colorScheme={colorScheme}
                  />
                  
                  <FileUploadField 
                    testId={test.id}
                    fileType="jpeg"
                    label="JPEG/JPG File Upload"
                    accept=".jpg,.jpeg,.png"
                    colorScheme={colorScheme}
                  />
                  
                  <FileUploadField 
                    testId={test.id}
                    fileType="meta"
                    label="Metadata File Upload"
                    accept=".xml,.json,.txt"
                    colorScheme={colorScheme}
                  />
                </div>
              </div>
            );
          })}

          {/* Save Button */}
          {formData.selectedTests.length > 0 && (
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSave}
                className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center space-x-2 shadow-lg"
              >
                <Save className="w-5 h-5" />
                <span>Save All Uploads</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FileUploads;